/**
 * RenewalTab — Phase 9E dedicated tab on the debtor profile.
 *
 * Shows the resolved renewal pathway (YES / NO / EXPIRED), a live
 * expiration/dormancy countdown, and lists the 10 most-recently generated
 * renewal documents. YES states get a "Generate Renewal Document" CTA that
 * opens the RenewalPreviewModal so the creditor can eyeball the pre-populated
 * PDF before saving. NO states get an amber "Attorney Required" info panel.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Calendar,
  Download,
  FileSignature,
  FileText,
  Info,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import Skeleton from "@/components/jea/Skeleton";
import RenewalPreviewModal from "@/components/debtors/RenewalPreviewModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const shortDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.round(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? "s" : ""} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years !== 1 ? "s" : ""} ago`;
};

const addYearsIso = (iso, years) => {
  if (!iso) return null;
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);
};

// ---------------------------------------------------------------------------
// Countdown card — shared across YES / NO
// ---------------------------------------------------------------------------
const CountdownCard = ({ deadlineIso, triggerType, severity = "neutral" }) => {
  const today = new Date().toISOString().slice(0, 10);
  const days = daysBetween(today, deadlineIso);
  const past = days != null && days < 0;
  const label = triggerType === "dormancy" ? "Dormancy in" : "Expiration in";
  const displayDays = days == null ? "—" : `${Math.abs(days)}`;
  const tone = past
    ? "border-danger/60 bg-danger/10"
    : severity === "warning"
    ? "border-warning/60 bg-warning/10"
    : severity === "success"
    ? "border-success/40 bg-success/5"
    : "border-border-default bg-bg-tertiary";
  return (
    <div
      className={cn("rounded-[8px] border p-3 flex items-center gap-3", tone)}
      data-testid="renewal-tab-countdown"
    >
      <Calendar size={16} className={past ? "text-danger" : "text-text-secondary"} />
      <div className="text-[13px] text-text-secondary">
        {past ? (
          <>
            <span className="text-danger font-semibold">Past deadline</span>
            {" · "}
            {label.replace("in", "was")}: <span className="jea-mono text-white">{shortDate(deadlineIso)}</span>
          </>
        ) : (
          <>
            {label}: <span className="jea-mono text-white font-semibold">{displayDays}d</span>{" "}
            <span className="text-text-muted">· deadline {shortDate(deadlineIso)}</span>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pathway header (YES)
// ---------------------------------------------------------------------------
const YesPathwayHeader = ({ resolved }) => {
  const cfg = resolved.config || {};
  return (
    <div className="flex items-start gap-3 flex-wrap">
      <div className="w-10 h-10 rounded-[10px] bg-success/15 border border-success/40 flex items-center justify-center shrink-0">
        <ShieldCheck size={18} className="text-success" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-white font-bold text-[16px]">
            {resolved.state_display} — Pro-Se Renewal Available
          </h3>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-success/15 text-success tracking-[0.06em] uppercase border border-success/40"
            data-testid="renewal-tab-yes-badge"
          >
            Pro-se renewal
          </span>
          {resolved.regime_label && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-bg-tertiary text-text-secondary tracking-[0.06em] uppercase border border-border-default"
              data-testid="renewal-tab-regime"
            >
              {resolved.regime_label} regime
            </span>
          )}
        </div>
        <p className="text-white text-[14px] leading-6 mt-2 max-w-[600px]">{resolved.message}</p>
        <div className="text-[12px] text-text-secondary mt-2">
          Trigger: <span className="text-white capitalize">{cfg.trigger_type}</span>
          {" · "}
          Window: <span className="text-white">{cfg.trigger_window_years} years</span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pathway header (NO)
// ---------------------------------------------------------------------------
const NoPathwayHeader = ({ resolved }) => (
  <div className="flex items-start gap-3 flex-wrap">
    <div className="w-10 h-10 rounded-[10px] bg-warning/15 border border-warning/40 flex items-center justify-center shrink-0">
      <ShieldAlert size={18} className="text-warning" />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-white font-bold text-[16px]">
          {resolved.state_display} — Attorney Required
        </h3>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-warning/15 text-warning tracking-[0.06em] uppercase border border-warning/40"
          data-testid="renewal-tab-no-badge"
        >
          Attorney required
        </span>
      </div>
      <p className="text-text-secondary text-[14px] leading-6 mt-2 max-w-[600px]">{resolved.message}</p>
      <div className="text-[12px] text-text-secondary mt-2 flex items-start gap-1.5">
        <Info size={12} className="text-warning mt-0.5 shrink-0" />
        This platform does not generate {resolved.state_display} renewal documents. Retain licensed counsel before your deadline.
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Pathway header (NO — neutral "no_pathway" display mode)
// ---------------------------------------------------------------------------
const NoPathwayNeutralHeader = ({ resolved }) => (
  <div className="flex items-start gap-3 flex-wrap">
    <div className="w-10 h-10 rounded-[10px] bg-bg-tertiary border border-border-default flex items-center justify-center shrink-0">
      <FileText size={18} className="text-text-secondary" />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-white font-bold text-[16px]">
          {resolved.state_display} — No Renewal Pathway
        </h3>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-text-muted/15 text-text-secondary tracking-[0.06em] uppercase border border-border-default"
          data-testid="renewal-tab-no-pathway-badge"
        >
          No pathway
        </span>
      </div>
      <p className="text-text-secondary text-[14px] leading-6 mt-2 max-w-[600px]">
        This state does not currently offer a post-judgment renewal mechanism.
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Pathway header (EXPIRED)
// ---------------------------------------------------------------------------
const ExpiredPathwayHeader = ({ resolved }) => (
  <div className="flex items-start gap-3 flex-wrap">
    <div className="w-10 h-10 rounded-[10px] bg-danger/15 border border-danger/40 flex items-center justify-center shrink-0">
      <AlertTriangle size={18} className="text-danger" />
    </div>
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-white font-bold text-[16px]">
          {resolved.state_display} — Judgment Permanently Expired
        </h3>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-danger/15 text-danger tracking-[0.06em] uppercase border border-danger/40"
          data-testid="renewal-tab-expired-badge"
        >
          Expired
        </span>
      </div>
      <p className="text-text-secondary text-[13px] mt-2 max-w-[600px]">{resolved.message}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Generated document row
// ---------------------------------------------------------------------------
const GeneratedDocRow = ({ doc, onRegenerate }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
    className="grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3 rounded-[8px] border border-border-default bg-bg-tertiary hover:border-accent-pink/40 transition-colors"
    data-testid={`renewal-tab-doc-row-${doc.id}`}
  >
    <div className="w-9 h-9 rounded-[8px] bg-accent-pink/10 border border-accent-pink/30 flex items-center justify-center">
      <FileSignature size={16} className="text-accent-pink" />
    </div>
    <div className="min-w-0">
      <div className="text-white text-[14px] font-semibold truncate">{doc.title}</div>
      <div className="text-[11px] text-text-secondary jea-mono mt-1">
        {shortDate(doc.generated_at)} · {relativeTime(doc.generated_at)}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onRegenerate(doc.document_type)}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 text-[12px] font-semibold transition-colors"
      data-testid={`renewal-tab-doc-regenerate-${doc.id}`}
      title="Re-generate with the latest debtor data"
    >
      <RefreshCw size={12} /> Re-generate
    </button>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Main tab component
// ---------------------------------------------------------------------------
const RenewalTab = ({ debtor }) => {
  const j = useMemo(() => debtor?.judgment || {}, [debtor]);
  const debtorId = debtor?.id;
  const state = j.state;
  const entryDate = j.date_entered;
  const priorRenewalRecorded = j.renewed === true;

  const [resolved, setResolved] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [nonce, setNonce] = useState(0);

  // Resolve renewal config
  useEffect(() => {
    if (!state) {
      setResolved(null);
      setConfigLoading(false);
      return;
    }
    let alive = true;
    setConfigLoading(true);
    apiClient
      .get(`/renewal/config/${state}`, {
        params: { entry_date: entryDate, prior_renewal_recorded: priorRenewalRecorded },
      })
      .then(({ data }) => { if (alive) setResolved(data); })
      .catch((e) => { if (alive) toast.error(formatApiError(e)); })
      .finally(() => { if (alive) setConfigLoading(false); });
    return () => { alive = false; };
  }, [state, entryDate, priorRenewalRecorded]);

  // Load generated docs
  useEffect(() => {
    if (!debtorId) return;
    let alive = true;
    setDocsLoading(true);
    apiClient
      .get(`/renewal/documents/${debtorId}`)
      .then(({ data }) => { if (alive) setDocs((data.documents || []).slice(0, 10)); })
      .catch(() => { if (alive) setDocs([]); })
      .finally(() => { if (alive) setDocsLoading(false); });
    return () => { alive = false; };
  }, [debtorId, nonce]);

  const refreshDocs = () => setNonce((n) => n + 1);

  if (!state || !entryDate) {
    return (
      <EmptyState
        icon={FileText}
        title="Renewal pathway unavailable"
        description="Add the judgment state and entry date on the profile to see the renewal pathway."
        testid="renewal-tab-empty"
      />
    );
  }

  if (configLoading || !resolved) {
    return (
      <div className="space-y-3" data-testid="renewal-tab-loading">
        <Skeleton className="h-24" />
        <Skeleton className="h-16" />
        <Skeleton className="h-12" />
      </div>
    );
  }

  const cfg = resolved.config || {};
  const isYes = resolved.renewal_pathway === "YES";
  const isNo = resolved.renewal_pathway === "NO";
  const isExpired = resolved.renewal_pathway === "EXPIRED";
  const isNoNeutral = isNo && resolved.no_pathway_display_mode === "no_pathway";
  const isNoAttorney = isNo && !isNoNeutral;
  const deadlineIso = entryDate && resolved.trigger_window_years
    ? addYearsIso(entryDate, resolved.trigger_window_years)
    : null;

  return (
    <div className="space-y-6" data-testid="renewal-tab">
      <JeaCard
        className={cn(
          isYes && "border-success/40 bg-success/5",
          isNoAttorney && "border-warning/40",
          isNoNeutral && "border-border-default",
          isExpired && "border-danger/60 bg-danger/10"
        )}
        data-testid="renewal-tab-pathway-card"
      >
        {isYes && <YesPathwayHeader resolved={resolved} />}
        {isNoAttorney && <NoPathwayHeader resolved={resolved} />}
        {isNoNeutral && <NoPathwayNeutralHeader resolved={resolved} />}
        {isExpired && <ExpiredPathwayHeader resolved={resolved} />}

        {!isExpired && !isNoNeutral && deadlineIso && (
          <div className="mt-4">
            <CountdownCard
              deadlineIso={deadlineIso}
              triggerType={resolved.trigger_type}
              severity={isNoAttorney ? "warning" : "success"}
            />
          </div>
        )}

        {isYes && (
          <div className="mt-5 flex flex-wrap gap-2">
            <JeaButton
              onClick={() =>
                setPreview({
                  document_type: cfg.document_type,
                  display_name: cfg.document_display_name,
                })
              }
              className="gap-2"
              data-testid="renewal-tab-generate-primary"
            >
              <FileText size={14} /> Generate {cfg.document_display_name}
            </JeaButton>
            {cfg.secondary_document_type && (
              <JeaButton
                variant="outline-pink"
                onClick={() =>
                  setPreview({
                    document_type: cfg.secondary_document_type,
                    display_name: cfg.secondary_document_display_name,
                  })
                }
                className="gap-2"
                data-testid="renewal-tab-generate-secondary"
              >
                <FileText size={14} /> Generate {cfg.secondary_document_display_name}
              </JeaButton>
            )}
          </div>
        )}
      </JeaCard>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-bold text-[15px]" data-testid="renewal-tab-history-heading">
            Generated Renewal Documents
          </h4>
          <span className="text-[11px] text-text-muted tracking-[0.06em] uppercase font-semibold">
            {docs.length} of last 10
          </span>
        </div>
        {docsLoading ? (
          <div className="space-y-2" data-testid="renewal-tab-history-loading">
            {[0, 1].map((i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : docs.length === 0 ? (
          <JeaCard className="text-text-secondary text-[13px]" data-testid="renewal-tab-history-empty">
            No renewal documents have been generated for this judgment yet.
            {isYes && " Click the button above to generate your first document."}
          </JeaCard>
        ) : (
          <div className="space-y-2" data-testid="renewal-tab-history-list">
            {docs.map((d) => (
              <GeneratedDocRow
                key={d.id}
                doc={d}
                onRegenerate={(docType) => setPreview({
                  document_type: docType,
                  display_name: d.title,
                })}
              />
            ))}
          </div>
        )}
      </div>

      <RenewalPreviewModal
        open={!!preview}
        debtorId={debtorId}
        documentType={preview?.document_type}
        documentDisplayName={preview?.display_name}
        onClose={() => {
          setPreview(null);
          refreshDocs();
        }}
      />
    </div>
  );
};

export default RenewalTab;
